import { useCallback, useRef, useState } from 'react'
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { isNotEmpty } from '../../utils/helpers'
import UseTranslation from '../../hooks/UseTranslation'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import useIosDropdownPlacement from '../../hooks/useIosDropdownPlacement'

const OfoqMultiAutoComplete = ({
  name,
  store,
  defaultOption,
  refState,
  nameConverter,
  required,
  customStyle,
  helperText,
  error,
  onBlur,
  isLoading,
  ...props
}) => {
  const splitName = name?.split('_')?.join(' ')
  const { t } = UseTranslation()

  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const iosPlacement = useIosDropdownPlacement(anchorRef, open)

  const handleAutocompleteOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleAutocompleteClose = useCallback(() => {
    setOpen(false)
  }, [])

  const sendSelectedItem = (value) => {
    refState(value)
  }

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />
  return (
    <FormControl
      ref={anchorRef}
      sx={[styles.formControlGrow, customStyle]}
    >
      <InputLabel
        htmlFor={name}
        variant="heading6"
        sx={styles.formInputLabel}
      >
        {splitName}
        {required && <TeamSnap style={styles.snapIcon} data-testid="teamsnap-icon" />}

      </InputLabel>

      <Autocomplete
        id={name}
        limitTags={1}
        selectOnFocus={false}
        isOptionEqualToValue={
          (options, value) => (options?.id === value?.id) ||
            (options?.dbid ? options?.dbid === value?.dbid : false)}
        defaultValue={isNotEmpty(defaultOption) ? defaultOption : []}
        value={isNotEmpty(defaultOption) ? defaultOption : []}
        sx={styles.multiAutoComplete}
        options={store || []}
        getOptionLabel={(optionList) => optionList.name || optionList.english_name || ''}
        popupIcon={<KeyboardArrowDownIcon />}
        onChange={(_, newValue) => sendSelectedItem(newValue)}
        multiple
        clearOnBlur={!iosPlacement.keepTypedText}
        loading={isLoading}
        loadingText={t?.Loading}
        noOptionsText={`${t?.NOT} ${splitName} ${t?.FOUND}`}
        renderOption={(prop, option, { selected }) => (
          <li {...prop}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={styles.checkBoxStyles}
              checked={selected}
            />
            {nameConverter ? nameConverter(option) : option.name || option.english_name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            placeholder={`${t?.Select} ${splitName}`}
            variant="outlined"
            fullWidth
            margin="dense"
            {...params}
            error={error}
            helperText={helperText}
            onBlur={onBlur}
            slotProps={{
              input: {
                ...params.InputProps,
                onFocus: (e) => {
                  params.inputProps?.onFocus?.(e)
                  requestAnimationFrame(() => {
                    const input = e.target
                    if (
                      input?.setSelectionRange &&
                      document.activeElement === input
                    ) {
                      const len = input.value.length
                      input.setSelectionRange(len, len)
                    }
                  })
                },
                endAdornment: (
                  <>
                    {(isLoading) ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        {...props}
        open={open}
        onOpen={handleAutocompleteOpen}
        onClose={handleAutocompleteClose}
        ListboxProps={{
          sx: iosPlacement.maxHeight ? { maxHeight: `${iosPlacement.maxHeight}px` } : undefined,
        }}
        slotProps={{
          // Upwards on iOS, clear of the keyboard; capped by the hook.
          popper: iosPlacement.upwards ? iosPlacement.popperProps : undefined,
          popupIndicator: {
            onMouseDown: (e) => {
              e.preventDefault()
            },
            onClick: (e) => {
              e.stopPropagation()
              if (open) {
                handleAutocompleteClose(e, 'toggleInput')
              } else {
                handleAutocompleteOpen(e)
              }
            },
          },
        }}
      />
    </FormControl>
  )
}

OfoqMultiAutoComplete.defaultProps = {
  defaultOption: [],
  nameConverter: null,
  required: false,
  customStyle: {},
  helperText: null,
  error: null,
  onBlur: null,
  props: null,
  isLoading: false,
};

const styles = {
  formControlGrow: {
    width: '100%',
    marginBottom: '20px',
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: 'translate(14px,12px) scale(1)',
    },
    '& .MuiFormControl-marginDense': {
      marginTop: '30px',
      marginBottom: '0',
      paddingRight: '0',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      backgroundColor: 'background.default',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: (theme) => `2px solid ${theme.palette.grey[200]}  !important`,
      transition: 'all 0.3s ease-in-out',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: '12px',
    },
    '& .MuiAlert-root': {
      width: '100%',
      marginBottom: 16,
    },
  },

  snapIcon: {
    marginInlineStart: '8px',
  },
  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'capitalize',
    transform: 'translate(0,0) scale(1)',
    color: 'text.primary',
    fontSize: '14px',
  },

  multiAutoComplete: {
    '& .MuiChip-label': {
      fontSize: '14px',
      paddingLeft: '8px',
      paddingRight: '8px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      height: '20px',
      color: 'text.primary',
    },

    '& .MuiChip-deleteIcon': {
      color: 'primary.main',
      fill: 'primary.main',
      width: '18px',
      margin: '0px 6px 0px -2px',
      cursor: 'pointer',
    },

    '& .MuiSvgIcon-root': {
      fill: 'currentColor',
      display: 'inline-block',
      fontSize: '1.5rem',
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      flexShrink: 0,
      userSelect: 'none',
    },

    '& .MuiAutocomplete-tag': {
      paddingRight: '5px',
      paddingBottom: '3px',
      borderRadius: '10px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'primary.main',
      backgroundColor: 'background.default',
      '& .MuiChip-deleteIcon': { fill: '#29cc8b' },
    },

    '& input': {
      fontSize: '14px',
      height: {
        xs: '4px',
        sm: '20px',
      },
    },
  },

  checkBoxStyles: {
    fontSize: '14px !important',
  },
}

export default OfoqMultiAutoComplete
